import React, { useContext, useLayoutEffect } from 'react';
import { Card, CardContent, Typography, Chip, Box, Grid, CardActions } from '@mui/material';

import styles from '../../../assets/styles/componentStyles/templateLibraryStyles/templateCards.module.scss';

import GlobalButton from '../../utility/globalButton/button';
import { useTemplateLibraryStore } from '../../../store/templateLibraryStore';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview/WhatsAppTemplatePreview';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createWhatsappTemplatePayloadForTemplateLibrary, getTemplateLibraryList } from '../../../models/axiosModel';
import { createBasicTemplateFromScratch } from './constant';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { useCarouselFunctions } from './utils/useCarouselFunctions';

const parseTemplatePayload = (jsonData: getTemplateLibraryList['json_data']): createWhatsappTemplatePayloadForTemplateLibrary | null => {
  if (jsonData == null || typeof jsonData === 'number' || typeof jsonData === 'boolean') return null;
  try {
    const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    return parsed as createWhatsappTemplatePayloadForTemplateLibrary;
  } catch {
    return null;
  }
};

const payloadForCreateTemplate = (payload: createWhatsappTemplatePayloadForTemplateLibrary): createWhatsappTemplatePayloadForTemplateLibrary => {
  const cloned = structuredClone(payload) as createWhatsappTemplatePayloadForTemplateLibrary;
  delete (cloned as any).whatsapp_template_id;
  return cloned;
};

const TemplateCards = () => {

  const { setFlagLoading } = useContext(UserContext) as UserContextPayload;

  const { isMobileScreen } = useCarouselFunctions();

  const filters = useTemplateLibraryStore((state) => state.filters);
  const templateLibraryList = useTemplateLibraryStore((state) => state.templateLibraryList);
  const getTemplateLibraryListApiCall = useTemplateLibraryStore((state) => state.getTemplateLibraryListApiCall);
  const setSelectedTemplate = useTemplateLibraryStore((state) => state.setSelectedTemplate);
  const setSelectedTemplateId = useTemplateLibraryStore((state) => state.setSelectedTemplateId);
  const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
  const whatsappTemplateButtonGenericData = useTemplateLibraryStore((state) => state.whatsappTemplateButtonGenericData);
  const getWhatsappTemplateButtonTypeApiCall = useTemplateLibraryStore((state) => state.getWhatsappTemplateButtonTypeApiCall);
  const interactiveTypeList = useTemplateLibraryStore((state) => state.interactiveTypeList);
  const getInteractiveTypeListApiCall = useTemplateLibraryStore((state) => state.getInteractiveTypeListApiCall);

  useLayoutEffect(() => {
    (async () => {
      if (templateLibraryList !== null) {
        return;
      }

      setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
      await getTemplateLibraryListApiCall();
      setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    })();
  }, [getTemplateLibraryListApiCall, templateLibraryList]);

  useLayoutEffect(() => {
    (async () => {
      if (!whatsappTemplateButtonGenericData) {
        await getWhatsappTemplateButtonTypeApiCall();
      }
    })();
  }, [whatsappTemplateButtonGenericData, getWhatsappTemplateButtonTypeApiCall]);

  useLayoutEffect(() => {
    (async () => {
      if (!interactiveTypeList) {
        await getInteractiveTypeListApiCall();
      }
    })();
  }, [interactiveTypeList, getInteractiveTypeListApiCall]);

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const id = searchParams.get('id') || searchParams.get('waba_id');

  const handleModifyNavigation = (template: getTemplateLibraryList) => {
    const previewPayload = parseTemplatePayload(template.json_data);
    const payloadToUse = previewPayload ?? (createBasicTemplateFromScratch(templateLibraryCategoryList ?? [], whatsappTemplateButtonGenericData ?? []) as createWhatsappTemplatePayloadForTemplateLibrary);

    setSelectedTemplate(payloadForCreateTemplate(payloadToUse));
    setSelectedTemplateId(template.template_library_id ?? '');

    navigate(`/channels/viewintegration/whatsapp/templatelibrary/modifyTemplate?id=${id}`);
  };

  const filteredTemplates = (templateLibraryList ?? []).filter((template: getTemplateLibraryList) => {
    const previewPayload = parseTemplatePayload(template.json_data);
    
    const name = previewPayload?.whatsapp_template_data?.whatsapp_template_display_name || template?.template_library_name || '';
    const useCases = template?.template_library_use_case_assignment?.map((u) => u?.template_library_use_case_name).filter(Boolean) ?? [];
    const industries = template?.template_library_industry_assignment?.map((i) => i?.template_library_industry_name).filter(Boolean) ?? [];

    const matchesUseCase = filters.useCase === 'All' || useCases.includes(filters.useCase);
    const matchesCategory = filters.category === 'All' || template?.whatsapp_template_category_name === filters.category;
    const matchesIndustry = filters.industry === 'All' || industries.includes(filters.industry);
    const matchesSearch = !filters.search || name?.toLowerCase().includes(filters.search.toLowerCase()) || template?.template_library_name?.toLowerCase().includes(filters.search.toLowerCase());

    const flagHideTemplate = template?.template_library_flag_hide ?? false;

    return matchesUseCase && matchesCategory && matchesIndustry && matchesSearch && !flagHideTemplate;
  });

  return (
    <Grid item xs={12} className={styles.templateCards}>
      <Grid container className={styles.templateCards__grid}>

        {filteredTemplates.length === 0 && (
          <Grid item xs={12} className={styles.templateCards__noTemplatesFoundWrapper}>
            <Typography className={styles.templateCards__noTemplatesFound} sx={{ width: "100%", textAlign: "center" }}>
              No templates found matching your criteria. Please try again with different filters.
            </Typography>
          </Grid>
        )}
        
        {filteredTemplates.length > 0 && filteredTemplates?.map((template: getTemplateLibraryList, index: number) => {
          
          const templateId = template?.template_library_id ?? `template-${index}`;
          const previewPayload = parseTemplatePayload(template?.json_data);
          const displayName = previewPayload?.whatsapp_template_data?.whatsapp_template_display_name || template?.template_library_name || '';
          const displayCategory = template?.whatsapp_template_category_name ?? 'Utility';

          return (
            <Grid item key={templateId} className={styles.templateCards__gridItem}>
              
              <Card className={`${styles.templateCard}`}>

                <Box className={styles.templateCard__preview}>
                  <Box className={styles.templateCard__preview__whatsapp}>
                    {previewPayload ? (
                      <WhatsAppTemplatePreview
                        selectedTemplate={structuredClone(previewPayload) as createWhatsappTemplatePayloadForTemplateLibrary}
                        compact={true}
                        editable={false}
                      />
                    ) : (
                      <Typography component="span" variant={isMobileScreen ? "caption" : "body2"} color="text.secondary" textAlign="center" mt="50%">
                        Unable to load preview
                      </Typography>
                    )}
                  </Box>
                </Box>

                <CardContent className={styles.templateCard__content}>
                  <Grid container className={styles.templateCard__header}>
                    <Typography className={styles.templateCard__title}>
                      {displayName}
                    </Typography>
                  </Grid>

                  <Grid container alignItems="center" justifyContent="space-between" className={styles.templateCard__footer}>
                    <Chip
                      size="small"
                      label={displayCategory}
                      className={`${styles.templateCard__chip} ${styles[`templateCard__chip--${displayCategory.toLowerCase()}`]}`}
                    />

                    <CardActions className={styles.templateCard__actions}>
                      <GlobalButton
                        buttonText="Modify and Use"
                        className="tertiaryButtonStyle"
                        onClick={(e) => {e.stopPropagation(); handleModifyNavigation(template);}}
                        sx={{ height: `30px`, fontSize: `12px !important`, borderRadius: `5px !important` }}
                      />
                    </CardActions>
                  </Grid>
                </CardContent>
                
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default TemplateCards;
